import { useAppSelector } from '@app/core/hooks/redux-hooks';
import { FC, useMemo } from 'react';
import { enumKeyByValue } from '@app/shared/helpers';
import {
  WaterfallSectionsErf,
  WaterfallSections,
} from '@app/shared/models/contracts/enums/shared-enums';
import classNames from 'classnames';
import { WaterfallTableExpandableRow } from '@app/modules/projects/outputs/pwerm2-summary-waterfall/waterfall-table/WaterfallTableExpandableRow';
import { WaterfallPartialTableProps } from '../waterfall-table/waterfall-shared';
import { useLocale } from '@app/core/hooks/useLocale';
import useTogglerPwermWaterfall from '@app/core/hooks/useTogglerPwermWaterfall';
import { InterimDistributionsSection } from './InterimDistributionsSection';
import { ExitDistributionsSection } from './ExitDistributionsSection';
import { InvestmentSection } from './InvestmentSection';
import { MomAndIrrSection } from './MomAndIrrSection';

export const Pwerm2WaterfallSponsorReturnPartialTable: FC<WaterfallPartialTableProps> = ({
  columnsCount,
}): JSX.Element => {
  const { l } = useLocale();
  const pwermWaterfallTableStates = useAppSelector(
    (state) => state.uiValues.pwermWaterfallTableExpandStates
  );
  const { handleToggle } = useTogglerPwermWaterfall(WaterfallSectionsErf.SponsorReturn);
  const results = useAppSelector((state) => state.pwerm2Calculation.calculatedResults);

  const hasAnyInterimDistributions = useMemo(
    () =>
      Object.values(results.waterfall.cases).some((caseItem) =>
        Object.values(caseItem.years).some(
          (year) =>
            year.cumulativeInstitutionalRedemptions !== 0 ||
            year.cumulativeInstitutionalCashInterest !== 0 ||
            year.cumulativeInstitutionalDividends !== 0 ||
            year.cumulativeInstitutionalInterimCashflows !== 0
        )
      ),
    [results.waterfall.cases]
  );

  return (
    <>
      <tbody data-testid="sponsor-return-expandable-row">
        <WaterfallTableExpandableRow
          title={l('_SponsorReturnTitle')}
          id={enumKeyByValue(WaterfallSections, WaterfallSections.SponsorReturn)}
          isOpen={pwermWaterfallTableStates[WaterfallSectionsErf.SponsorReturn]}
          setIsOpen={handleToggle}
          colCount={columnsCount}
        />
      </tbody>
      <tbody
        className={classNames('table-primary__table-section', {
          'table-primary__table-section--collapsed':
            !pwermWaterfallTableStates[WaterfallSectionsErf.SponsorReturn],
        })}
        data-testid="sponsor-return-section">
        <InterimDistributionsSection
          columnsCount={columnsCount}
          hasInterimDistributions={hasAnyInterimDistributions}
        />
        <ExitDistributionsSection
          columnsCount={columnsCount}
          hasInterimDistributions={hasAnyInterimDistributions}
        />
        <InvestmentSection columnsCount={columnsCount} />
        <MomAndIrrSection columnsCount={columnsCount} />
      </tbody>
    </>
  );
};
